import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { getIntegrationByType } from '@/components/organisms/Integrations';
import Tile from '@/components/organisms/Tile/Tile';
import { Badge } from '@/components/ui/badge';
import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Integration } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { timeFromNow } from '@/util/dateUtils';

import styles from './IntegrationTile.module.scss';

interface Props {
  integration: Integration;
}
const IntegrationTile = ({ integration }: Props) => {
  const { type, name, active, integration_id } = integration ?? {};
  const config = getIntegrationByType(type);
  const Icon = config?.Icon;
  const { t } = useTranslation();
  const lastActive = integration?.last_session_at;
  const slug = useSelector(selectAccountSlug);
  const deskId = useDeskId();

  const link = `/${slug}/environments/${deskId}/integrations/${integration_id}`;
  return (
    <Link aria-label={name} className={styles.link} to={link}>
      <Tile className={styles.tile}>
        <div className={styles.wrapper}>
          {Icon && <Icon />}
          <div className={styles.content}>
            <div className={styles.info}>
              <span className="text-sm leading-5 font-semibold text-foreground">
                {t(getChannelKey(type))}
              </span>
              <span className="text-xs leading-5 font-medium text-foreground-muted">
                {t('common.last_used')}:{' '}
                <span className="font-regular text-xs leading-5 text-foreground">
                  {lastActive ? (
                    <TimestampTooltip timestamp={lastActive}>
                      {timeFromNow(lastActive)}
                    </TimestampTooltip>
                  ) : (
                    '-'
                  )}
                </span>
              </span>
            </div>
            <div className={styles.status}>
              {integration.status === 'verified' ? (
                <Badge type="secondary" flavour={active ? 'successful' : 'neutral'}>
                  {active ? t('rules.active') : t('rules.inactive')}
                </Badge>
              ) : (
                <span className={cn('text-sm leading-5 font-normal', styles.setup)}>
                  {t('home.finish_setup')}
                </span>
              )}
            </div>
          </div>
        </div>
      </Tile>
    </Link>
  );
};

export default IntegrationTile;
